/**
 * Copyright (C) 2026 Guido Berhoerster <guido+voxit@berhoerster.name>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import * as voxitAdminWebClient from "./voxit-admin-web-client.js";
import { VoxitStatementEditor, VoxitAdminFrontend } from "./voxit-admin-frontend.js";

class VoxitConversationEditor {
    constructor() {
        this.dialog = document.querySelector("#conversation-editor")
        this.dialog.addEventListener("beforetoggle", this);
        this.dialog.addEventListener("submit", this);
        this.dialog.addEventListener("toggle", this);

        this.formElement = document.forms["conversation-form"];
    }

    open(conversation) {
        if (conversation) {
            this.dialog.querySelector("#create-title").hidden = true;
            this.dialog.querySelector("#edit-title").hidden = false;
        } else {
            this.dialog.querySelector("#create-title").hidden = false;
            this.dialog.querySelector("#edit-title").hidden = true;
            conversation = new voxitAdminWebClient.Conversation()
        }
        this.formElement["conversation_id"].value = conversation.id;
        this.formElement["topic"].value = conversation.topic;
        this.formElement["description"].value = conversation.description;
        this.formElement["is_active"].checked = conversation.isActive;
        this.formElement["statements_allowed"].checked = conversation.statementsAllowed;
        this.formElement["results_enabled"].checked = conversation.resultsEnabled;
        this.formElement["strict_moderation"].checked = conversation.strictModeration;

        this.dialog.showModal();
    }

    close() {
        this.dialog.close();
    }

    onSubmit() {
        const conversation = new voxitAdminWebClient.Conversation(
            {
                "id": this.formElement["conversation_id"].value,
                "topic": this.formElement["topic"].value.trim(),
                "description": this.formElement["description"].value.trim(),
                "is_active": this.formElement["is_active"].checked,
                "statements_allowed": this.formElement["statements_allowed"].checked,
                "results_enabled": this.formElement["results_enabled"].checked,
                "strict_moderation": this.formElement["strict_moderation"].checked,
            }
        );
        const event = new CustomEvent("submitconversation", {
            detail: {
                conversation
            }
        });
        this.dialog.dispatchEvent(event);
    }

    handleEvent(event) {
        console.debug(event)
        switch (event.type) {
        case "submit":
            event.preventDefault();
            this.onSubmit();
            break;
        }
    }
}

class VoxitConversationTranslationEditor {
    translations = new Map();

    constructor(submitCallback) {
        this.dialog = document.querySelector("#conversation-translation-editor")
        this.dialog.addEventListener("submit", this);

        this.languageFormElement = document.forms["translation-language-selector"];
        this.languageFormElement.addEventListener("submit", this);

        this.translationFormElement = document.forms["conversation-translation-form"];

        this.submitCallback = submitCallback;
    }

    open(conversationID, translations) {
        this.translations = new Map();
        for (const translation of translations) {
            this.translations.set(translation.language, translation);
        }

        this.languageFormElement.querySelector("fieldset").disabled = false;

        for (const optionElement of this.languageFormElement.querySelectorAll("select > hr ~ option")) {
            optionElement.remove()
        }
        const options = [];
        for (const translation of translations) {
            const optionElement = document.createElement("option");
            optionElement.value = optionElement.textContent = translation.language;
            options.push(optionElement);
        }
        this.languageFormElement.querySelector("select > hr").after(...options);

        this.translationFormElement.querySelector("#language-message").hidden = true;
        this.translationFormElement.querySelector("fieldset").disabled = true;
        this.translationFormElement.reset();
        this.translationFormElement["conversation_id"].value = conversationID;

        this.dialog.showModal();
    }

    close() {
        this.dialog.close();
    }

    showInvalidLanguageTagError() {
        this.translationFormElement.querySelector("#language-message").hidden = false;
    }

    onLanguageSubmit() {
        this.languageFormElement.querySelector("fieldset").disabled = true;
        this.translationFormElement.querySelector("fieldset").disabled = false;

        const language = this.languageFormElement["language"].value;
        this.translationFormElement["language"].value = language;
        if (language !== "") {
            this.translationFormElement["language"].disabled = true;
            this.translationFormElement["topic"].value = this.translations.get(language).topic;
            this.translationFormElement["description"].value = this.translations.get(language).description;
        } else {
            this.translationFormElement["language"].disabled = false;
        }
    }

    onSubmit() {
        const conversationID = this.translationFormElement["conversation_id"].value;
        const translation = new voxitAdminWebClient.ConversationTranslation({
            "topic": this.translationFormElement["topic"].value.trim(),
            "description": this.translationFormElement["description"].value.trim(),
            "language": this.translationFormElement["language"].value.trim(),
        });
        const event = new CustomEvent("submitconversationtranslation", {
            detail: {
                conversationID,
                translation,
            }
        });
        this.dialog.dispatchEvent(event);
    }

    handleEvent(event) {
        console.debug(event)
        switch (event.type) {
        case "submit":
            if (event.target === this.languageFormElement) {
                event.preventDefault();

                this.onLanguageSubmit();
            } else if (event.target === this.translationFormElement) {
                event.preventDefault();

                this.onSubmit();
            }
            break;
        }
    }
}

class VoxitAdminConversationsFrontend extends VoxitAdminFrontend {
    conversations = new Map();

    constructor(client) {
        super(client);

        this.conversationEditor = new VoxitConversationEditor();
        this.conversationEditor.dialog.addEventListener("command", this);
        this.conversationEditor.dialog.addEventListener("submitconversation", this);

        this.conversationTranslationEditor = new VoxitConversationTranslationEditor();
        this.conversationTranslationEditor.dialog.addEventListener("command", this);
        this.conversationTranslationEditor.dialog.addEventListener("submitconversationtranslation", this);

        this.statementEditor = new VoxitStatementEditor();
        this.statementEditor.dialog.addEventListener("command", this);
        this.statementEditor.dialog.addEventListener("submitstatement", this);

        this.tableElement = document.querySelector("#conversations-table");
        this.tableElement.addEventListener("click", this);

        this.statusOpenTemplateElement = document.querySelector("#status-open-template");
        this.statusClosedTemplateElement = document.querySelector("#status-closed-template");

        this.rowTemplateElement = document.querySelector("#conversation-row-template");
    }

    async init() {
        this.updateConversationsList();
    }

    async onLogin() {
        this.updateConversationsList();
    }

    async updateConversationsList() {
        let conversations;
        try {
            conversations = await this.client.fetchConversations();
        } catch (error) {
            this.handleClientError(error);
            console.error("Failed to update conversations:", error);
            return;
        }

        this.conversations = new Map();
        const rows = [];
        for (const conversation of conversations) {
            this.conversations.set(conversation.id, conversation);

            const clone = document.importNode(this.rowTemplateElement.content, true);
            clone.querySelector(".conversation-id").textContent = conversation.id;
            clone.querySelector(".conversation-id-button > button").value = conversation.id;
            clone.querySelector(".conversation-topic").textContent = conversation.topic;
            const timeElement = clone.querySelector(".conversation-published time");
            timeElement.dateTime = conversation.created;
            timeElement.textContent = conversation.created.toLocaleDateString("en");
            let statusElement;
            if (conversation.isActive) {
                statusElement = document.importNode(this.statusOpenTemplateElement.content, true);
            } else {
                statusElement = document.importNode(this.statusClosedTemplateElement.content, true);
            }
            clone.querySelector(".conversation-status").appendChild(statusElement);
            for (const buttonElement of clone.querySelectorAll("button[command^='--open']")) {
                buttonElement.value = conversation.id;
            }
            rows.push(clone);
        }

        this.tableElement.querySelector("tbody").replaceChildren(...rows);
    }

    async onConversationIDCopyClick(id) {
        await navigator.clipboard.writeText(id);
    }

    async onConversationSubmit(conversation) {
        try {
            if (conversation.id === "") {
                await this.client.createConversation(conversation);
            } else {
                await this.client.updateConversation(conversation);
            }
        } catch (error) {
            this.handleClientError(error);
            console.error("Failed to create/update conversation", error);
            return;
        }

        this.conversationEditor.close();
        await this.updateConversationsList();
    }

    async onConversationTranslationSubmit(conversationID, translation) {
        try {
            await this.client.updateConversationTranslation(conversationID, translation);
        } catch (error) {
            if (error instanceof voxitAdminWebClient.InvalidLanguageTagError) {
                this.conversationTranslationEditor.showInvalidLanguageTagError();
                return;
            }
            this.handleClientError(error);
            console.error("Failed to create/update conversation translation", error);
            return;
        }

        this.conversationTranslationEditor.close();
    }

    async onStatementSubmit(conversationID, statement) {
        try {
            await this.client.addStatement(conversationID, statement);
        } catch(error) {
            if (error instanceof voxitAdminWebClient.StatementExistsError) {
                this.statementEditor.showStatementExistsError();
                return;
            } else {
                this.handleClientError(error);
                console.error("Failed to add statement:", error);
            }
        }

        this.statementEditor.close();
    }

    openConversationEditor(id) {
        this.conversationEditor.open(this.conversations.get(id));
    }

    async openConversationTranslationEditor(conversationID) {
        let translations;
        try {
            translations = await this.client.fetchConversationTranslations(conversationID);
        } catch (error) {
            this.handleClientError(error);
            console.error("Failed to fetch conversation translations", error);
            return;
        }
        this.conversationTranslationEditor.open(conversationID, translations);
    }

    openStatementEditor(conversationID) {
        this.statementEditor.open(conversationID);
    }

    async handleEvent(event) {
        console.debug(event);
        switch (event.type) {
        case "click":
            if (event.target.classList.contains("copy-button")) {
                await this.onConversationIDCopyClick(event.target.value);
            }
            break;
        case "command":
            if (event.command === "--open-conversation-editor") {
                event.preventDefault();

                this.openConversationEditor(event.source.value);
            } else if (event.command === "--open-conversation-translation-editor") {
                event.preventDefault();

                this.openConversationTranslationEditor(event.source.value);
            } else if (event.command === "--open-statement-editor") {
                event.preventDefault();

                this.openStatementEditor(event.source.value);
            }
            break;
        case "submitconversation":
            await this.onConversationSubmit(event.detail.conversation);
            break;
        case "submitconversationtranslation":
            await this.onConversationTranslationSubmit(event.detail.conversationID, event.detail.translation);
            break;
        case "submitstatement":
            await this.onStatementSubmit(event.detail.conversationID, event.detail.statement);
            break;
        }
        super.handleEvent(event);
    }
}

// allow setting a default externally
const defaultBaseURL = typeof DEFAULT_BASE_URL !== "undefined" ? DEFAULT_BASE_URL : "";
const baseURL = new URLSearchParams(window.location.search).get("base_url") ?? defaultBaseURL;
let url;
try {
    url = new URL(baseURL);
} catch(error) {
    if (!(error instanceof TypeError)) {
        throw error;
    }
}

if (typeof url === "undefined") {
    document.querySelector("#base-url-dialog").showModal();
} else {
    const client = new voxitAdminWebClient.AdminClient(baseURL);
    const app = new VoxitAdminConversationsFrontend(client);
    app.init();
}
